import classNames from 'classnames';
import { ReactNode, useCallback, useMemo } from 'react';

import { PropsWithClassname } from '@/app/types';

export interface TabsSelectOption<ValueType = string> {
  value: ValueType | undefined;
  label: string;
}

export interface TabsSelectProps<ValueType = string> extends PropsWithClassname {
  value: ValueType | undefined;
  onChange: (nextValue: ValueType) => void;
  options: TabsSelectOption<ValueType>[];
  label?: string;
  disabled?: boolean;
  isErrorVisible?: boolean;
  errorMessage?: ReactNode;
}

function TabsSelect<ValueType extends string | number | readonly string[] = string>({
  value,
  options,
  onChange,
  label,
  disabled,
  isErrorVisible,
  errorMessage,
  className,
}: TabsSelectProps<ValueType>) {
  const flexBasis = useMemo(() => {
    const percentage = Math.round((100 / options.length) * 100) / 100;

    return `${percentage}%`;
  }, [options]);

  const handleChange = useCallback(
    (nextValue?: ValueType) => {
      if (!nextValue) {
        return;
      }

      onChange(nextValue);
    },
    [onChange],
  );

  return (
    <div className={classNames(className, { 'has-error': isErrorVisible })}>
      {!!label && <label className="block mb-[8px] font-medium text-[16px] leading-[24px]">{label}</label>}
      {!!errorMessage && isErrorVisible && <div className="text-warning font-medium mb-[8px]">{errorMessage}</div>}
      <div className="w-full flex border rounded-full p-[4px]">
        {options.map((option) => (
          <div
            key={String(option.value)}
            onClick={() => handleChange(option.value)}
            className={classNames(
              'px-[8px] py-[12px] rounded-full transition-all font-medium text-center text-[14px]',
              {
                'bg-primary-light text-primary-mid': value === option.value && !disabled,
                'bg-neutral-light text-neutral-mid': value === option.value && disabled,
                'bg-white text-neutral-dark': value !== option.value && !disabled,
                'bg-white text-neutral-mid': value !== option.value && disabled,
                'cursor-pointer': !disabled,
                'cursor-not-allowed': disabled,
              },
            )}
            style={{
              flexBasis: flexBasis,
            }}
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TabsSelect;
